import React from 'react';
import { Link } from 'react-router-dom';
import type { Entity } from '@einhaus/project314-types';
import { getEntityLink } from '@/utils/entityHelpers';

interface EntityLinkProps {
    children?: React.ReactNode;
    className?: string;
    entity: Entity | { entityId: number; entityType: string; entityName?: string; entitySymbol?: string };
    showName?: boolean;
    showSymbol?: boolean;
}

/**
 * A reusable component for rendering links to entity pages
 */
const EntityLink: React.FC<EntityLinkProps> = ({ children, className = '', entity, showName = true, showSymbol = true }) => {
    // Generate the URL
    const url = getEntityLink(entity);

    // Determine if we're working with an Entity object or something with entityId/entityType
    const entityId = 'id' in entity ? entity.id : entity.entityId;

    // Get symbol and name depending on the object type
    const symbol = 'symbol' in entity ? entity.symbol : entity.entitySymbol;
    const name = 'name' in entity ? entity.name : entity.entityName;

    // If children are provided, use those instead of the default entity name/symbol
    if (children) {
        return (
            <Link className={className} to={url}>
                {children}
            </Link>
        );
    }

    // Determine text content based on available data
    let content;

    if (showSymbol && symbol) {
        // Show symbol and optionally name
        content = (
            <>
                <span className="fw-bold">{symbol}</span>
                {showName && name && <span className="ms-1">({name})</span>}
            </>
        );
    } else if (showName && name) {
        // Show just the name
        content = <span>{name}</span>;
    } else {
        // Fallback
        content = <span className="text-muted">Entity ID: {entityId}</span>;
    }

    return (
        <Link className={className} to={url}>
            {content}
        </Link>
    );
};

export default EntityLink;
